import '../../Core/widget/TabPanel.js';
import './projecteditor/ProjectEditorAdvancedTab.js';
import './projecteditor/ProjectEditorDescriptionTab.js';
import './projecteditor/ProjectEditorGeneralTab.js';
import TaskEditorBase from '../../SchedulerPro/widget/TaskEditorBase.js';

/**
 * @module Gantt/widget/ProjectEditor
 */

/**
 * Provides a UI to edit the project settings in a dialog window. The widget is used internally by
 * the {@link Gantt/feature/ProjectEdit project edit feature} and you don't normally
 * need to deal with this class directly.
 *
 * This demo shows how to use the ProjectEditor as a standalone widget:
 *
 * {@inlineexample Gantt/widget/ProjectEditor.js}
 *
 * ## Project editor customization
 *
 * To append Widgets to any of the built-in tabs, use the `items` config. The Project editor contains tabs by default.
 * Each tab contains built-in widgets: text fields, date fields, etc.
 *
 * | Tab ref          | Text                                                          | Weight | Description                                                       |
 * |------------------|---------------------------------------------------------------|--------|-------------------------------------------------------------------|
 * | `generalTab`     | {@link Gantt/widget/projecteditor/ProjectEditorGeneralTab}    | 100    | Basic configuration: name, start/end dates, schedule from         |
 * | `descriptionTab` | {@link Gantt/widget/projecteditor/ProjectEditorDescriptionTab}| 200    | A text area to add a description to the selected project          |
 * | `advancedTab`    | {@link Gantt/widget/projecteditor/ProjectEditorAdvancedTab}   | 300    | Advanced options: hours per day, days per week and days per month |
 *
 * @classtype projecteditor
 * @extends SchedulerPro/widget/TaskEditorBase
 *
 */
export default class ProjectEditor extends TaskEditorBase {

    //region Config

    static $name = 'ProjectEditor';

    static type = 'projecteditor';

    static configurable =  {
        rootElement  : document.body,
        modal        : true,
        scrollAction : 'realign',

        /**
         * The project model that will be edited with the project editor.
         * @config {Gantt.model.ProjectModel}
         */
        project : null,

        autoUpdateFields : true,

        bbar : {
            // When readOnly, child buttons are hidden
            hideWhenEmpty : true,

            defaults : {
                localeClass : this
            },

            items : {
                // We don't support removing project so hiding the corresponding button
                deleteButton : false
            }
        },

        title : 'L{projectInformation}',

        items : {
            tabs : {
                type       : 'tabpanel',
                flex       : '1 0 100%',
                autoHeight : true,

                layoutStyle : {
                    alignItems   : 'stretch',
                    alignContent : 'stretch'
                },

                items : {
                    generalTab : {
                        type   : 'projecteditorgeneraltab',
                        weight : 100
                    },
                    descriptionTab : {
                        type   : 'projecteditordescriptiontab',
                        weight : 200
                    },
                    advancedTab : {
                        type   : 'projecteditoradvancedtab',
                        weight : 300
                    }
                }
            }
        },

        width : 400
    };

    updateProject(project) {
        this.loadProject(project);

        this.setupAutoUpdateFieldsListeners(this.autoUpdateFields);
    }

    updateAutoUpdateFields(value) {
        this.setupAutoUpdateFieldsListeners(value);
    }

    //endregion

    //region Internal

    onProjectModelChange({ source }) {
        const { items : childTabs } = this.widgetMap.tabs;

        // Since we delegate "autoUpdateRecord : true" to each individual tab
        // - trigger reaction to a project field change for each tab
        for (let i = 0, { length } = childTabs; i < length; i++) {
            childTabs[i].onRecordUpdated({ record : source });
        }
    }

    setupAutoUpdateFieldsListeners(enable) {
        this.detachListeners('projectModelChange');

        // Default "autoUpdateFields" config doesn't work since project is not part of any store.
        // So if autoUpdateFields is enabled we have to handle it by listening to "projectModelChange" event
        const project = this._project;

        if (enable && project) {
            project.ion({
                name               : 'projectModelChange',
                projectModelChange : this.onProjectModelChange,
                thisObj            : this
            });
        }
    }

    hide() {
        this.detachListeners('projectModelChange');

        return super.hide(...arguments);
    }

    /**
     * Loads a project model into the editor
     *
     * @param {Gantt.model.ProjectModel} record
     */
    loadProject(record, highlightChanges = false) {
        this.loadEvent(record, highlightChanges);
    }

    loadEvent(record, highlightChanges = false) {
        this.isLoadingProject = true;
        super.loadEvent(record, highlightChanges);
        this.isLoadingProject = false;
    }

    //endregion
}

// Register this widget type with its Factory
ProjectEditor.initClass();
